import React, { useState, useMemo, useRef, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useGetTasksQuery, useGetProjectsQuery, useUpdateTaskMutation, useDeleteTaskMutation } from "../../services/rtkClient";
import CreateTask from "../CreateTask/CreateTask";
import Navbar from "../../components/Navbar";
import "./Listpage.css";
import {
  LayoutGrid,
  Folder,
  ClipboardList as Tasks,
  Plus,
  ChevronRight,
  ChevronDown,
  ChevronLeft,
  MoreHorizontal,
  Filter,
  ArrowDownWideNarrow as Sort,
  Search,
  Download,
  Eye,
  Pencil as Edit,
  Trash2 as Trash,
  Menu,
  Users,
  History
} from "lucide-react";

const getCurrentUser = () => {
  try {
    const storedUser = localStorage.getItem("user");
    if (storedUser) return JSON.parse(storedUser);
  } catch (err) { }
  return null;
};

const Listpage = () => {
  const navigate = useNavigate();
  const currentUserInfo = getCurrentUser();
  const currentUserName = currentUserInfo ? (currentUserInfo.firstName ? `${currentUserInfo.firstName} ${currentUserInfo.lastName || ''}`.trim() : (currentUserInfo.username || currentUserInfo.email || "Current User")) : "Current User";
  const location = useLocation();

  const projectName = location.state?.projectName || sessionStorage.getItem('projectName') || "";

  const statusMap = {
    todo: "To Do",
    inprogress: "In Progress",
    review: "In Review",
    done: "Completed",
  };

  const initialTasks = useMemo(() => {
    const locTasks = location.state?.tasks;
    if (locTasks) {
      // If tasks is already an array (e.g. passed directly), map it
      if (Array.isArray(locTasks)) {
        return locTasks.map((task) => ({
          id: task.id,
          task: task.title || task.task || "Untitled",
          assignee: task.assignee || "Unassigned",
          reporter: task.reporter || currentUserName,
          priority: task.priority || "Medium",
          status: task.status || "To Do",
          resolution:
            task.resolution ||
            (task.status === "done" || task.status === "Completed"
              ? "Resolved"
              : "Unresolved"),
          created: task.date || task.created || "Feb 10, 2026",
          updated: task.updated || task.date || "Feb 10, 2026",
          timeSpent: task.timeSpent || 0,
        }));
      }

      // If tasks is an object keyed by status (the original expectation)
      if (typeof locTasks === "object") {
        return Object.entries(locTasks).flatMap(([statusKey, taskArray]) => {
          // If the entry isn't an array, try to handle a single object or skip it
          if (!Array.isArray(taskArray)) {
            if (taskArray && typeof taskArray === "object") {
              const t = taskArray;
              return [
                {
                  id: t.id,
                  task: t.title || t.task || "Untitled",
                  assignee: t.assignee || "Unassigned",
                  reporter: t.reporter || currentUserName,
                  priority: t.priority || "Medium",
                  status: statusMap[statusKey] || statusKey,
                  resolution:
                    statusKey === "done"
                      ? "Resolved"
                      : t.resolution || "Unresolved",
                  created: t.date || t.created || "Feb 10, 2026",
                  updated: t.updated || t.date || "Feb 10, 2026",
                  timeSpent: t.timeSpent || 0,
                },
              ];
            }
            return [];
          }

          return taskArray.map((task) => ({
            id: task.id,
            task: task.title || task.task || "Untitled",
            assignee: task.assignee || "Unassigned",
            reporter: task.reporter || currentUserName,
            priority: task.priority || "Medium",
            status: statusMap[statusKey] || statusKey,
            resolution: statusKey === "done" ? "Resolved" : "Unresolved",
            created: task.date || task.created || "Feb 10, 2026",
            updated: task.updated || task.date || "Feb 10, 2026",
            timeSpent: task.timeSpent || 0,
          }));
        });
      }
    }
    return [];
  }, [location.state]);

  const getCurrentUserEmail = () => {
    return currentUserInfo?.email || currentUserInfo?.username || "default";
  };

  const { data: serverProjectsData } = useGetProjectsQuery();
  const [updateTaskMutation] = useUpdateTaskMutation();
  const [deleteTaskMutation] = useDeleteTaskMutation();
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);

  const recentProjects = useMemo(() => {
    const raw = serverProjectsData?.data || serverProjectsData?.schema || (Array.isArray(serverProjectsData) ? serverProjectsData : []);
    return raw.map(p => ({
      id: p.id || p.projectId,
      name: p.name || p.projectName || "Unnamed Project"
    }));
  }, [serverProjectsData]);

  const currentProjectId = useMemo(() => {
    if (location.state?.projectId) return Number(location.state.projectId);
    const stored = sessionStorage.getItem('projectId');
    if (stored) return Number(stored);
    if (recentProjects.length > 0) {
      return Number(recentProjects[0].id || recentProjects[0].projectId);
    }
    return null;
  }, [recentProjects, location.state?.projectId]);

  const { data: serverTasksData, isLoading: tasksLoading, refetch: refetchTasks } = useGetTasksQuery(currentProjectId, { skip: !currentProjectId });

  const serverTasks = useMemo(() => {
    const raw = Array.isArray(serverTasksData) ? serverTasksData : [];
    return raw
      .filter(t => !currentProjectId || Number(t.projectId || t.project_id) === currentProjectId)
      .map(t => {
        return {
          ...t,
          id: t.id || t.issueId || `KAN-${t.id || t.issueId}`,
          title: t.summary || t.task || t.title || "Untitled",
          task: t.summary || t.task || t.title || "Untitled",
          assignee: t.assigneeName || t.assigneeId || "Unassigned",
          reporter: t.reporterName || t.reporterId || currentUserName,
          priority: t.priority || "Medium",
          status: statusMap[t.status?.toLowerCase().replace(/\s/g, '')] || t.status || "To Do",
          resolution: t.resolution || (t.status === "done" || t.status === "Completed" ? "Resolved" : "Unresolved"),
          created: t.createdAt ? new Date(t.createdAt).toLocaleDateString() : (t.date || "Feb 10, 2026"),
          updated: t.updatedAt ? new Date(t.updatedAt).toLocaleDateString() : (t.updated || "Feb 10, 2026"),
          timeSpent: Number(t.timeSpent) || 0,
          originalEstimate: Number(t.originalEstimate) || 0,
        };
      });
  }, [serverTasksData, currentProjectId, currentUserName]);


  const [editingTaskId, setEditingTaskId] = useState(null);
  const [editDraft, setEditDraft] = useState({});
  const [viewTask, setViewTask] = useState(null);
  const [openMenuId, setOpenMenuId] = useState(null);
  const menuRef = useRef(null);
  const [isSidebarVisible, setIsSidebarVisible] = useState(false);
  const [projectsOpen, setProjectsOpen] = useState(false);
  const [statusOpen, setStatusOpen] = useState(false);
  const [currentProjectName, setCurrentProjectName] = useState(projectName || "New Workspace");
  const [filterCriteria, setFilterCriteria] = useState({ type: null, value: null });
  const [showTimeSpentPopup, setShowTimeSpentPopup] = useState(false);
  const [pendingStatusChange, setPendingStatusChange] = useState(null);
  const [timeSpentInput, setTimeSpentInput] = useState("");


  useEffect(() => {
    if (!location.state?.projectName && !sessionStorage.getItem('projectName') && recentProjects.length > 0) {
      setCurrentProjectName(recentProjects[0].name);
      sessionStorage.setItem('projectName', recentProjects[0].name);
    }
    if (location.state?.projectName) {
      setCurrentProjectName(location.state.projectName);
      sessionStorage.setItem('projectName', location.state.projectName);
    }
  }, [recentProjects, location.state?.projectName]);

  const filteredTasks = useMemo(() => {
    const baseTasks = serverTasks.length > 0 ? serverTasks : initialTasks;
    if (!filterCriteria.type || !filterCriteria.value) return baseTasks;

    return baseTasks.filter((task) => {
      if (filterCriteria.type === "Project") {
        // Assuming task has a projectName or similar field, or we just filter by the project name in the state
        // For now, let's assume we filter by "currentProjectName" if type is Project
        return true; // We'll update currentProjectName which might be used elsewhere
      }
      if (filterCriteria.type === "Status") {
        return task.status === filterCriteria.value;
      }
      return true;
    });
  }, [serverTasks, initialTasks, filterCriteria]);

  const handleSubItemClick = (type, value) => {
    if (type === "Project") {
      setCurrentProjectName(value);
      sessionStorage.setItem('projectName', value);
      // Find project ID to update sessionStorage
      const proj = recentProjects.find(p => p.name === value);
      if (proj) sessionStorage.setItem('projectId', String(proj.id));
      setFilterCriteria({ type: "Project", value });
    } else if (type === "Status") {
      if (value === "All") {
        setFilterCriteria({ type: null, value: null });
      } else {
        setFilterCriteria({ type: "Status", value });
      }
    }
  };

  const handleExport = () => {
    if (filteredTasks.length === 0) {
      alert("No tasks to export");
      return;
    }

    const headers = ["ID", "Task", "Assignee", "Reporter", "Priority", "Status", "Resolution", "Created", "Updated"];
    const csvRows = filteredTasks.map(t => [
      t.id,
      `"${t.task}"`,
      `"${t.assignee}"`,
      `"${t.reporter}"`,
      t.priority,
      t.status,
      t.resolution,
      `"${t.created}"`,
      `"${t.updated}"`
    ].join(","));

    const csvContent = [headers.join(","), ...csvRows].join("\n");
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.setAttribute("href", url);
    link.setAttribute("download", `tasks_${currentProjectName.replace(/\s+/g, '_').toLowerCase()}.csv`);
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case "High":
        return "#FF5630";
      case "Medium":
        return "#FFAB00";
      case "Low":
        return "#36B37E";
      default:
        return "#42526E";
    }
  };

  const getStatusStyle = (status) => {
    switch (status) {
      case "To Do":
        return { bg: "#F4F5F7", color: "#42526E" };
      case "In Progress":
        return { bg: "#E6EFFC", color: "#0052CC" };
      case "In Review":
        return { bg: "#EAE6FF", color: "#6554C0" };
      case "Completed":
        return { bg: "#E3FCEF", color: "#36B37E" };
      case "On Hold":
        return { bg: "#FFF0B3", color: "#FFAB00" };
      default:
        return { bg: "#F4F5F7", color: "#42526E" };
    }
  };

  const getResolutionStyle = (res) => {
    if (res === "Resolved") return { color: "#36B37E", fontWeight: "bold" };
    if (res === "Pending") return { color: "#0052CC" };
    return { color: "#42526E" };
  };

  // ── Helpers to convert flat list ↔ grouped localStorage format ───────────
  const statusToColKey = (status) => {
    switch (status) {
      case "In Progress": return "inprogress";
      case "In Review": return "review";
      case "Completed": return "done";
      default: return "todo";
    }
  };

  // Local storage synchronization removed per user request
  const syncToLocalStorage = (flatTasks) => {
    // API updates should be added here
  };

  const handleDelete = async (id) => {
    if (window.confirm("Are you sure you want to delete this task?")) {
      try {
        const numericId = parseInt(id.toString().replace("KAN-", ""));
        await deleteTaskMutation(isNaN(numericId) ? id : numericId).unwrap();
        refetchTasks();
      } catch (err) {
        console.error("Delete Task Error:", err);
        alert("Failed to delete task. Please try again.");
      }
    }
  };

  const handleStatusChange = async (id, newStatus) => {
    if (newStatus === "Completed") {
      setPendingStatusChange({ id, newStatus });
      setShowTimeSpentPopup(true);
      return;
    }

    try {
      const numericId = parseInt(id.toString().replace("KAN-", ""));
      const originalId = isNaN(numericId) ? id : numericId;
      const originalTask = Array.isArray(serverTasksData)
        ? serverTasksData.find(t => (t.id || t.issueId) === originalId || `KAN-${t.id || t.issueId}` === id || t.id === id)
        : null;

      const payload = originalTask ? { ...originalTask } : {};
      payload.issueId = originalTask ? (originalTask.id || originalTask.issueId) : originalId;
      payload.status = statusToColKey(newStatus);

      await updateTaskMutation(payload).unwrap();

      refetchTasks();
    } catch (err) {
      console.error("Status Change Error:", err);
    }
  };

  const handleTimeSpentSubmit = async () => {
    if (!pendingStatusChange) return;
    const { id, newStatus } = pendingStatusChange;

    try {
      const numericId = parseInt(id.toString().replace("KAN-", ""));
      const originalId = isNaN(numericId) ? id : numericId;
      const originalTask = Array.isArray(serverTasksData)
        ? serverTasksData.find(t => (t.id || t.issueId) === originalId || `KAN-${t.id || t.issueId}` === id || t.id === id)
        : null;

      const payload = originalTask ? { ...originalTask } : {};
      payload.issueId = originalTask ? (originalTask.id || originalTask.issueId) : originalId;
      payload.status = statusToColKey(newStatus);
      payload.timeSpent = Number(timeSpentInput) || 0;

      await updateTaskMutation(payload).unwrap();

      refetchTasks();
      setShowTimeSpentPopup(false);
      setPendingStatusChange(null);
      setTimeSpentInput("");
    } catch (err) {
      console.error("Time Spent Submit Error:", err);
    }
  };

  const handlePriorityChange = async (id, newPriority) => {
    try {
      const numericId = parseInt(id.toString().replace("KAN-", ""));
      const originalId = isNaN(numericId) ? id : numericId;
      const originalTask = Array.isArray(serverTasksData)
        ? serverTasksData.find(t => (t.id || t.issueId) === originalId || `KAN-${t.id || t.issueId}` === id || t.id === id)
        : null;

      const payload = originalTask ? { ...originalTask } : {};
      payload.issueId = originalTask ? (originalTask.id || originalTask.issueId) : originalId;
      payload.priority = newPriority.toLowerCase();

      await updateTaskMutation(payload).unwrap();

      refetchTasks();
    } catch (err) {
      console.error("Priority Change Error:", err);
    }
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setOpenMenuId(null);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleView = (task) => {
    setViewTask(task);
    setOpenMenuId(null);
  };

  const handleEdit = (task) => {
    setEditingTaskId(task.id);
    setEditDraft({ ...task });
    setOpenMenuId(null);
  };

  const handleEditSave = async (id) => {
    try {
      const numericId = parseInt(id.toString().replace("KAN-", ""));
      await updateTaskMutation({
        issueId: isNaN(numericId) ? id : numericId,
        summary: editDraft.task,
        assigneeId: editDraft.assigneeId || 1, // Need to handle ID mapping if possible, or just use name
        priority: editDraft.priority?.toLowerCase(),
        status: statusToColKey(editDraft.status)
      }).unwrap();

      refetchTasks();
      setEditingTaskId(null);
      setEditDraft({});
    } catch (err) {
      console.error("Edit Save Error:", err);
    }
  };

  const handleEditCancel = () => {
    setEditingTaskId(null);
    setEditDraft({});
  };

  const handleAddTaskToList = (newTaskData) => {
    refetchTasks();
    setIsCreateModalOpen(false);
  };

  return (
    <div className="list-page-wrapper">
      <Navbar onCreate={() => setIsCreateModalOpen(true)} />
      <div className="list-page-container">
        {/* Sidebar */}
        {isSidebarVisible && (
          <aside className="lp-sidebar">
            <div className="lp-sidebar-nav">
              <div
                className="lp-nav-item active"
                onClick={() => navigate()}
                style={{ cursor: "pointer" }}
              >
                <span className="lp-nav-icon">
                  <LayoutGrid size={18} />
                </span>
                Dashboard
              </div>
            </div>

            <div className="lp-nav-section-header">
              <span>New Project</span>
              <span className="lp-section-add" onClick={() => navigate('/onboarding')} style={{ cursor: 'pointer' }}>
                <Plus size={14} />
              </span>
            </div>
            <div className="lp-sidebar-nav secondary">
              <div
                className="lp-nav-item"
                onClick={() => setProjectsOpen(!projectsOpen)}
                style={{ cursor: "pointer" }}
              >
                <span className="lp-nav-icon">
                  <Folder size={18} />
                </span>
                Projects
                <span className="lp-nav-arrow">
                  {projectsOpen ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                </span>
              </div>
              {projectsOpen && (
                <div className="lp-sub-items">
                  {recentProjects.map((proj, idx) => (
                    <div
                      key={idx}
                      className="lp-sub-item"
                      onClick={() => handleSubItemClick("Project", proj.name)}
                    >
                      {proj.name}
                    </div>
                  ))}
                </div>
              )}

              <div
                className="lp-nav-item"
                onClick={() => setStatusOpen(!statusOpen)}
                style={{ cursor: "pointer" }}
              >
                <span className="lp-nav-icon">
                  <Tasks size={18} />
                </span>
                Status
                <span className="lp-nav-arrow">
                  {statusOpen ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                </span>
              </div>
              {statusOpen && (
                <div className="lp-sub-items">
                  <div
                    className="lp-sub-item"
                    onClick={() => handleSubItemClick("Status", "All")}
                  >
                    All
                  </div>
                  <div
                    className="lp-sub-item"
                    onClick={() => handleSubItemClick("Status", "Completed")}
                  >
                    Completed
                  </div>
                  <div
                    className="lp-sub-item"
                    onClick={() => handleSubItemClick("Status", "In Progress")}
                  >
                    In Progress
                  </div>
                  <div
                    className="lp-sub-item"
                    onClick={() => handleSubItemClick("Status", "On Hold")}
                  >
                    On Hold
                  </div>
                </div>
              )}
            </div>

            <div className="lp-sidebar-footer">
              <button className="lp-invite-btn">
                <Users size={16} /> Invite People
              </button>
            </div>
          </aside>
        )}

        {/* Main Content Area */}
        <main className="lp-main-content">
          <header className="lp-header">
            <div className="lp-header-top">
              <button
                className="sidebar-toggle-btn"
                onClick={() => setIsSidebarVisible(!isSidebarVisible)}
                title={isSidebarVisible ? "Hide Sidebar" : "Show Sidebar"}
              >
                <Menu size={18} />
              </button>
              <div className="lp-breadcrumb">
                <Folder size={16} className="lp-breadcrumb-icon" /> {projectName}{" "}
                <MoreHorizontal size={14} className="lp-breadcrumb-more" />
              </div>
            </div>
            <div className="lp-header-title">
              <div className="lp-project-avatar">
                <Tasks size={20} />
              </div>
              <h1>{currentProjectName}</h1>
              <div className="lp-view-toggle">
                <LayoutGrid size={18} />
              </div>
              <MoreHorizontal size={18} className="lp-header-more" />
            </div>

            <div className="lp-tabs">
              <div
                className="lp-tab"
                onClick={() =>
                  navigate("/summary", { state: { projectId: currentProjectId, projectName } })
                }
              >
                Summary
              </div>
              <div
                className="lp-tab"
                onClick={() => navigate("/tasks", { state: { projectId: currentProjectId, projectName } })}
              >
                Board
              </div>
              <div className="lp-tab active">Tasks List</div>
              <div className="lp-tab" onClick={() => navigate("/timeline", { state: { projectId: currentProjectId, projectName } })}>Timeline</div>
              <div className="lp-tab" onClick={() => navigate("/calendar", { state: { projectId: currentProjectId, projectName } })}>Calendar</div>

            </div>
          </header>

          <div className="lp-toolbar">
            <div className="lp-toolbar-left">
              <button className="lp-tool-btn">
                <Filter size={16} /> Filter
              </button>
              <button className="lp-tool-btn">
                <Sort size={16} /> Sort
              </button>
              <div className="lp-search-container">
                <Search size={16} className="lp-search-icon" />
                <input
                  type="text"
                  placeholder="Search work..."
                  className="lp-search-input"
                />
              </div>
              {filterCriteria.value && (
                <button
                  className="lp-tool-btn"
                  onClick={() => setFilterCriteria({ type: null, value: null })}
                  style={{ color: '#0052CC', fontWeight: 'bold' }}
                >
                  Clear filter: {filterCriteria.value}
                </button>
              )}
            </div>
            <div className="lp-toolbar-right">
              <button className="lp-tool-btn" onClick={handleExport}>
                <Download size={16} /> Export{" "}
                <ChevronDown size={12} style={{ marginLeft: "4px" }} />
              </button>
            </div>
          </div>

          <div className="lp-table-container">
            <table className="lp-table">
              <thead>
                <tr>
                  <th>
                    <input type="checkbox" />
                  </th>
                  <th>ID</th>
                  <th>Project / Task</th>
                  <th>Assignee</th>
                  <th>Reporter</th>
                  <th>Priority</th>
                  <th>Status</th>
                  <th>Resolution</th>
                  <th>
                    Created <ChevronDown size={14} />
                  </th>
                  <th>Updated</th>
                  <th>Spent</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredTasks.map((task) => {
                  const isEditing = editingTaskId === task.id;
                  return (
                    <tr key={task.id} style={isEditing ? { background: "#FFFAE6" } : {}}>
                      <td><input type="checkbox" /></td>
                      <td className="lp-task-id">{task.id}</td>
                      <td>
                        {isEditing ? (
                          <input
                            className="lp-inline-input"
                            value={editDraft.task}
                            onChange={(e) => setEditDraft({ ...editDraft, task: e.target.value })}
                          />
                        ) : (
                          <div className="lp-task-info">
                            <Tasks size={16} className="lp-task-icon" />
                            {task.task}
                          </div>
                        )}
                      </td>
                      <td>
                        {isEditing ? (
                          <input
                            className="lp-inline-input"
                            value={editDraft.assignee}
                            onChange={(e) => setEditDraft({ ...editDraft, assignee: e.target.value })}
                          />
                        ) : (
                          <div className="lp-user-cell">
                            <div className="lp-user-avatar-small">
                              {task.assignee === "Unassigned" ? <Users size={14} /> : String(task.assignee).charAt(0)}
                            </div>
                            {task.assignee}
                          </div>
                        )}
                      </td>
                      <td>
                        <div className="lp-user-cell">
                          <div className="lp-user-avatar-small reporter">
                            {String(task.reporter).split(" ").map((n) => n[0]).join("")}
                          </div>
                          {task.reporter}
                        </div>
                      </td>
                      <td>
                        <div className="lp-priority-cell">
                          <div className="lp-priority-indicator" style={{ borderTopColor: getPriorityColor(isEditing ? editDraft.priority : task.priority) }}></div>
                          <select
                            className="lp-priority-select"
                            value={isEditing ? editDraft.priority : task.priority}
                            onChange={(e) =>
                              isEditing
                                ? setEditDraft({ ...editDraft, priority: e.target.value })
                                : handlePriorityChange(task.id, e.target.value)
                            }
                          >
                            <option value="High">High</option>
                            <option value="Medium">Medium</option>
                            <option value="Low">Low</option>
                          </select>
                        </div>
                      </td>
                      <td>
                        <div className="lp-status-dropdown-wrapper">
                          <select
                            className="lp-status-select"
                            style={{
                              backgroundColor: getStatusStyle(isEditing ? editDraft.status : task.status).bg,
                              color: getStatusStyle(isEditing ? editDraft.status : task.status).color,
                            }}
                            value={isEditing ? editDraft.status : task.status}
                            onChange={(e) =>
                              isEditing
                                ? setEditDraft({ ...editDraft, status: e.target.value })
                                : handleStatusChange(task.id, e.target.value)
                            }
                          >
                            <option value="To Do">TO DO</option>
                            <option value="In Progress">IN PROGRESS</option>
                            <option value="In Review">IN REVIEW</option>
                            <option value="Completed">COMPLETED</option>
                            <option value="On Hold">ON HOLD</option>
                          </select>
                        </div>
                      </td>
                      <td style={getResolutionStyle(task.resolution)}>
                        {task.resolution === "Resolved" ? "✓ Resolved" : task.resolution}
                      </td>
                      <td className="lp-date-cell">{task.created}</td>
                      <td className="lp-date-cell">{task.updated}</td>
                      <td className="lp-spent-cell" style={{ fontWeight: 'bold', color: '#0052CC' }}>
                        {(task.status === "Completed" || task.status === "done") && task.timeSpent > 0 ? `${task.timeSpent}h` : '-'}
                      </td>
                      <td>
                        {isEditing ? (
                          <div className="lp-actions-cell">
                            <button className="lp-save-btn" onClick={() => handleEditSave(task.id)}>Save</button>
                            <button className="lp-cancel-btn" onClick={handleEditCancel}>Cancel</button>
                          </div>
                        ) : (
                          <div className="lp-actions-cell" style={{ position: "relative" }}>
                            <Eye size={16} title="View" onClick={() => handleView(task)} />
                            <Edit size={16} title="Edit" onClick={() => handleEdit(task)} />
                            <Trash size={16} className="lp-delete-icon" title="Delete" onClick={() => handleDelete(task.id)} />
                            <MoreHorizontal
                              size={16}
                              title="More options"
                              onClick={() => setOpenMenuId(openMenuId === task.id ? null : task.id)}
                            />
                            {openMenuId === task.id && (
                              <div className="lp-dropdown-menu" ref={menuRef}>
                                <div className="lp-dropdown-item" onClick={() => handleView(task)}>
                                  <Eye size={14} /> View Details
                                </div>
                                <div className="lp-dropdown-item" onClick={() => handleEdit(task)}>
                                  <Edit size={14} /> Edit Task
                                </div>
                                <div className="lp-dropdown-item lp-dropdown-danger" onClick={() => { handleDelete(task.id); setOpenMenuId(null); }}>
                                  <Trash size={14} /> Delete Task
                                </div>
                              </div>
                            )}
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* View Task Modal */}
          {viewTask && (
            <div className="lp-modal-overlay" onClick={() => setViewTask(null)}>
              <div className="lp-modal" onClick={(e) => e.stopPropagation()}>
                <div className="lp-modal-header">
                  <div className="lp-modal-title">
                    <Tasks size={18} className="lp-task-icon" />
                    <span>{viewTask.task}</span>
                  </div>
                  <button className="lp-modal-close" onClick={() => setViewTask(null)}>✕</button>
                </div>
                <div className="lp-modal-body">
                  <div className="lp-modal-row"><span className="lp-modal-label">ID</span><span>{viewTask.id}</span></div>
                  <div className="lp-modal-row"><span className="lp-modal-label">Assignee</span><span>{viewTask.assignee}</span></div>
                  <div className="lp-modal-row"><span className="lp-modal-label">Reporter</span><span>{viewTask.reporter}</span></div>
                  <div className="lp-modal-row">
                    <span className="lp-modal-label">Priority</span>
                    <span style={{ color: getPriorityColor(viewTask.priority), fontWeight: 600 }}>{viewTask.priority}</span>
                  </div>
                  <div className="lp-modal-row">
                    <span className="lp-modal-label">Status</span>
                    <span className="lp-status-badge" style={{ backgroundColor: getStatusStyle(viewTask.status).bg, color: getStatusStyle(viewTask.status).color }}>
                      {viewTask.status}
                    </span>
                  </div>
                  <div className="lp-modal-row"><span className="lp-modal-label">Resolution</span><span style={getResolutionStyle(viewTask.resolution)}>{viewTask.resolution}</span></div>
                  <div className="lp-modal-row"><span className="lp-modal-label">Created</span><span>{viewTask.created}</span></div>
                  <div className="lp-modal-row"><span className="lp-modal-label">Updated</span><span>{viewTask.updated}</span></div>
                </div>
                <div className="lp-modal-footer">
                  <button className="lp-modal-edit-btn" onClick={() => { handleEdit(viewTask); setViewTask(null); }}><Edit size={14} /> Edit Task</button>
                  <button className="lp-modal-close-btn" onClick={() => setViewTask(null)}>Close</button>
                </div>
              </div>
            </div>
          )}

          <footer className="lp-table-footer">
            <div className="lp-footer-left">
              <button className="lp-create-new-btn" onClick={() => setIsCreateModalOpen(true)}>
                <Plus size={16} /> Create New
              </button>
              <span className="lp-entry-info">
                Showing 1 to {filteredTasks.length} of {filteredTasks.length} entries
              </span>
            </div>
            <div className="lp-pagination">
              <button className="lp-pag-btn">
                <ChevronLeft size={16} /> Previous
              </button>
              <button className="lp-pag-num active">1</button>
              <button className="lp-pag-num">2</button>
              <button className="lp-pag-btn">
                Next <ChevronRight size={16} />
              </button>
            </div>
            <div className="lp-rows-per-page">
              Rows per page:
              <select>
                <option>10</option>
                <option>20</option>
                <option>50</option>
              </select>
            </div>
          </footer>

          <div className="lp-status-legend">
            <div className="lp-legend-item">
              <div className="lp-legend-dot todo"></div> To Do
            </div>
            <div className="lp-legend-item">
              <div className="lp-legend-dot in-progress"></div> In Progress
            </div>
            <div className="lp-legend-item">
              <div className="lp-legend-dot in-review"></div> In Review
            </div>
            <div className="lp-legend-item">
              <div className="lp-legend-dot completed"></div> Completed
            </div>
            <div className="lp-legend-item">
              <div className="lp-legend-dot on-hold"></div> On Hold
            </div>
          </div>
        </main>

        {showTimeSpentPopup && (
          <div className="lp-modal-overlay">
            <div className="lp-modal" style={{ maxWidth: '400px' }}>
              <div className="lp-modal-header">
                <div className="lp-modal-title">Log Time Spent</div>
                <button className="lp-modal-close" onClick={() => { setShowTimeSpentPopup(false); setPendingStatusChange(null); }}>✕</button>
              </div>
              <div className="lp-modal-body" style={{ padding: '20px' }}>
                <p style={{ marginBottom: '15px', color: '#42526E' }}>How many hours did you spend on this task?</p>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <input
                    type="number"
                    className="lp-inline-input"
                    style={{ width: '100%', padding: '8px', border: '2px solid #dfe1e6', borderRadius: '4px' }}
                    value={timeSpentInput}
                    onChange={(e) => setTimeSpentInput(e.target.value)}
                    autoFocus
                  />
                  <span style={{ color: '#6B778C', fontWeight: 'bold' }}>h</span>
                </div>
              </div>
              <div className="lp-modal-footer">
                <button className="lp-modal-close-btn" onClick={() => { setShowTimeSpentPopup(false); setPendingStatusChange(null); }}>Cancel</button>
                <button className="lp-save-btn" style={{ padding: '8px 16px' }} onClick={handleTimeSpentSubmit}>Complete Task</button>
              </div>
            </div>
          </div>
        )}

        {isCreateModalOpen && (
          <CreateTask
            onClose={() => setIsCreateModalOpen(false)}
            onAddTask={handleAddTaskToList}
            initialColumn="todo"
          />
        )}
      </div>
    </div>
  );
};

export default Listpage;
